import { TreeView, TreeViewExpandChangeEvent, processTreeViewItems } from '@progress/kendo-react-treeview';
import { TextBox, TextBoxChangeEvent, TextBoxHandle, InputPrefix, InputSuffix } from '@progress/kendo-react-inputs';
import { DropDownButton, DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import { ContextMenu, MenuSelectEvent } from '@progress/kendo-react-layout';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { StorageItem, StorageKind } from '../../constants/document';
import { getAuthorizations } from '../../reducers/user/selectors';
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut';
import { collectFolderTexts, filterTreeByText } from '../../utils';
import SharedLinkCreator from '../shared-links/SharedLink';
import AddFolder from './AddFolder';
import RenameItem from './RenameItem';
import styles from './TreeViewDocuments.module.css';
import TreeViewItem from './TreeViewItem';
import UploadFile from './UploadFile';

/**
 * Returns a JSX span with the first occurrence of `shortcutChar`
 * (case-insensitive) underlined inside `text`. Used as a visual cue in the
 * action menus so users discover the corresponding keyboard shortcut.
 * Falls back to plain text if the character isn't in the word.
 *
 * Children are kept on a single JSX line on purpose: putting them on
 * separate lines makes Babel emit `" "` (literal space) between them,
 * which would render as `R ename` instead of `Rename`.
 */
const renderUnderlinedLabel = (text: string, shortcutChar: string): React.ReactElement => {
  const idx = text.toLowerCase().indexOf(shortcutChar.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <span>{text.substring(0, idx)}<u>{text[idx]}</u>{text.substring(idx + 1)}</span>;
};

/**
 * Walks `items` depth-first and returns the Kendo TreeView "hierarchical
 * index" string (e.g. `"0_2_1"` = root → 3rd child → 2nd grandchild) for
 * the node whose `path` matches `targetPath`. Used to drive the visual
 * selection of the TreeView from the React `selectedFile.path` instead of
 * relying on a separate state synced from click events — this way the
 * highlight stays in sync when an item is selected programmatically (e.g.
 * after restoring the persisted selection from `localStorage` on mount).
 */
const findHierarchicalIndex = (
  items: any[] | undefined,
  targetPath: string,
  prefix = '',
): string | null => {
  if (!items) return null;
  for (let i = 0; i < items.length; i++) {
    const currentIndex = prefix ? `${prefix}_${i}` : `${i}`;
    if (items[i].path === targetPath) return currentIndex;
    const inner = findHierarchicalIndex(items[i].items, targetPath, currentIndex);
    if (inner) return inner;
  }
  return null;
};

const readStoredExpandIds = (key: string | undefined, fallback: string[]): string[] => {
  if (!key) return fallback;
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return fallback;
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed) && parsed.every((id) => typeof id === 'string')) {
      return parsed;
    }
  } catch {
    /* ignore parse / privacy-mode errors */
  }
  return fallback;
};

const TreeViewDocuments = ({
  items,
  selectFile,
  selectedFile,
  addNewFolder,
  addNewDocument,
  renameDocument,
  renameFolder,
  deleteDocument,
  deleteFolder,
  deleteContentFolder,
  downloadDocument,
  downloadFolder,
  persistExpandKey,
}: any) => {
  const rootFolderName = process.env.REACT_APP_THEME_ROOT_FOLDER ?? 'MotoGP INFORMATION';
  const [expand, setExpand] = useState<any>(() => ({
    ids: readStoredExpandIds(persistExpandKey, [rootFolderName]),
    idField: 'text',
  }));

  useEffect(() => {
    if (!persistExpandKey) return;
    try {
      localStorage.setItem(persistExpandKey, JSON.stringify(expand.ids));
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [expand.ids, persistExpandKey]);

  // Drop expand ids that no longer point to any folder in the current tree
  // (e.g. the folder was deleted or renamed). Prevents stale entries from
  // ghost-expanding unrelated folders that happen to share a name later on.
  useEffect(() => {
    if (!items || items.length === 0) return;
    const validTexts = new Set(collectFolderTexts(items));
    setExpand((prev: any) => {
      const cleaned = prev.ids.filter((id: string) => validTexts.has(id));
      if (cleaned.length === prev.ids.length) return prev;
      return { ...prev, ids: cleaned };
    });
  }, [items]);
  const [newFolderVisible, setNewFolderVisible] = useState<boolean>(false);
  const [newFileVisible, setNewFileVisible] = useState<boolean>(false);
  const [renameItemVisible, setRenameItemVisible] = useState<any>({
    visible: false,
    title: '',
    item: null,
    callBack: null,
  });
  const [sharedLinkVisible, setSharedLinkVisible] = useState<any>({ visible: false, item: null, callBack: null });
  const [selectedPath, setSelectedPath] = useState<string>();
  const [filterQuery, setFilterQuery] = useState<string>('');

  const visibleItems = useMemo(
    () => filterTreeByText(items ?? [], filterQuery),
    [items, filterQuery],
  );

  // Derive Kendo's selection (hierarchical index strings) directly from
  // the selectedFile prop, so the visual highlight reflects programmatic
  // selection (e.g. the persisted-path restore on mount), not just clicks.
  const selectedItemIds = useMemo(() => {
    if (!selectedFile?.path) return undefined;
    const idx = findHierarchicalIndex(visibleItems, selectedFile.path);
    return idx ? [idx] : undefined;
  }, [selectedFile, visibleItems]);

  // Once a valid selection exists for the first time, focus the TreeView
  // root so keyboard navigation (arrow keys, Enter) works without requiring
  // an initial click. Kendo's internal `onFocus` resolves to the
  // `tabbableItemId` (the first item by default), so the keyboard cursor
  // lands at the root of the tree even if the persisted-path restore put
  // the visual highlight deeper in the hierarchy. Skipped if focus is
  // currently somewhere else (typing in an input, an open dialog, ...) so
  // we don't steal it.
  const hasInitialFocusedRef = useRef(false);
  useEffect(() => {
    if (hasInitialFocusedRef.current) return;
    if (!selectedFile?.path) return;
    if (document.activeElement && document.activeElement !== document.body) return;
    const id = window.requestAnimationFrame(() => {
      const treeView = treeContainerRef.current?.querySelector<HTMLElement>('.k-treeview');
      if (treeView) {
        treeView.focus();
        hasInitialFocusedRef.current = true;
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedFile]);

  const effectiveExpand = useMemo(
    () =>
      filterQuery.trim()
        ? { ids: collectFolderTexts(visibleItems), idField: 'text' }
        : expand,
    [filterQuery, visibleItems, expand],
  );

  const onFilterChange = useCallback((e: TextBoxChangeEvent) => {
    setFilterQuery(String(e.value ?? ''));
  }, []);

  const onClearFilter = useCallback(() => setFilterQuery(''), []);

  // ----- Keyboard shortcuts (`/` focus search, `Esc` clear search) ----------
  const searchBoxRef = useRef<TextBoxHandle>(null);

  const focusSearchBox = useCallback(() => {
    searchBoxRef.current?.element?.focus();
    searchBoxRef.current?.element?.select();
  }, []);

  useKeyboardShortcut({ key: '/', handler: focusSearchBox });

  // `Esc` clears the search but ONLY when the user is actively focused on
  // the search input — otherwise it could swallow Esc presses meant for
  // closing dialogs / popups elsewhere in the page.
  const handleEscape = useCallback(() => {
    if (document.activeElement === searchBoxRef.current?.element && filterQuery) {
      setFilterQuery('');
    }
  }, [filterQuery]);

  useKeyboardShortcut({
    key: 'Escape',
    handler: handleEscape,
    ignoreWhenTyping: false,
    preventDefault: false,
  });
  // --------------------------------------------------------------------------

  // ----- Context menu (right-click on tree items) ---------------------------
  // The actual `onContextMenuSelect` callback is defined further down, after
  // the local dialog-opening wrappers (`onClickRenameDocument`,
  // `onClickShareDocument`, ...) so it can reuse them. Here we only set up
  // the state, the gating, and the trigger.
  const authorizations = useSelector(getAuthorizations);
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    item: StorageItem | null;
    offset: { left: number; top: number };
  }>({ visible: false, item: null, offset: { left: 0, top: 0 } });

  const userCanEdit = useCallback(
    (target: StorageItem) =>
      authorizations.some((a) =>
        target.path === undefined ? false : target.path.startsWith(a.resource) && a.edit,
      ),
    [authorizations],
  );

  const openContextMenuAt = useCallback(
    (target: StorageItem, clientX: number, clientY: number) => {
      // Documents always show a menu (Download is universal). Folders only
      // show a menu when the user has edit rights, since none of the folder
      // actions are available to non-admins.
      const isDocument = target.kind === StorageKind.document;
      if (!isDocument && !userCanEdit(target)) return;
      setContextMenu({
        visible: true,
        item: target,
        offset: { left: clientX, top: clientY },
      });
    },
    [userCanEdit],
  );

  // Walks the tree (depth-first) to find the item whose path matches
  // `targetPath`. Used by the contextmenu delegation handler to map a DOM
  // row back to its React StorageItem.
  const findItemByPath = useCallback(
    (nodes: StorageItem[] | undefined, targetPath: string): StorageItem | null => {
      if (!nodes) return null;
      for (const node of nodes) {
        if (node.path === targetPath) return node;
        const inner = findItemByPath(node.items as StorageItem[] | undefined, targetPath);
        if (inner) return inner;
      }
      return null;
    },
    [],
  );

  const treeContainerRef = useRef<HTMLDivElement>(null);

  // Event delegation: a single native `contextmenu` listener on the tree
  // container catches right-clicks anywhere inside any row — including the
  // empty space to the right of the text and the expand/collapse arrow —
  // not just the inline `tree-item-label` span. Walks up to the closest
  // Kendo row, finds the label inside it, reads its data-path, and
  // dispatches to openContextMenuAt.
  useEffect(() => {
    const root = treeContainerRef.current;
    if (!root) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const row = target.closest(
        '.k-treeview-leaf, .k-treeview-mid, .k-treeview-top',
      );
      if (!row) return;
      const label = row.querySelector<HTMLElement>('.tree-item-label');
      if (!label) return;
      const path = label.dataset.path;
      if (!path) return;
      const storageItem = findItemByPath(items, path);
      if (!storageItem) return;
      e.preventDefault();
      openContextMenuAt(storageItem, e.clientX, e.clientY);
    };
    root.addEventListener('contextmenu', handler);
    return () => root.removeEventListener('contextmenu', handler);
  }, [items, openContextMenuAt, findItemByPath]);

  const closeContextMenu = useCallback(
    () => setContextMenu((prev) => ({ ...prev, visible: false })),
    [],
  );

  const contextMenuItems = useMemo(() => {
    const target = contextMenu.item;
    if (!target) return [];
    // For Kendo Menu, `render` only replaces the text span — the icon is
    // still rendered separately by Kendo, so we just return the underlined
    // label here without rebuilding the icon.
    const buildItem = (text: string, iconName: string, action: string, shortcut?: string) => ({
      text,
      icon: iconName,
      data: { action },
      render: shortcut ? () => renderUnderlinedLabel(text, shortcut) : undefined,
    });
    if (target.kind === StorageKind.document) {
      // Edit-only actions appear first (most frequent for admins, kept near
      // the cursor); Download goes last and is always available regardless
      // of edit rights.
      const items: any[] = [];
      if (userCanEdit(target)) {
        items.push(buildItem('Rename', 'edit-tools', 'rename', 'r'));
        items.push(buildItem('Delete', 'delete', 'delete', 'd'));
        items.push(buildItem('Share', 'share', 'share', 's'));
      }
      items.push(buildItem('Download', 'download', 'download', 'w'));
      return items;
    }
    const folderItems: any[] = [];
    if (userCanEdit(target)) {
      folderItems.push(buildItem('New folder', 'folder-add', 'addFolder', 'n'));
      folderItems.push(buildItem('Upload file', 'file-add', 'addFile', 'u'));
      folderItems.push(buildItem('Rename', 'edit-tools', 'rename', 'r'));
      folderItems.push(buildItem('Delete content', 'files', 'deleteContent', 'o'));
      folderItems.push(buildItem('Delete folder', 'delete', 'delete', 'd'));
      folderItems.push(buildItem('Share', 'share', 'share', 's'));
    }
    folderItems.push(buildItem('Download as ZIP', 'download', 'downloadFolder', 'z'));
    return folderItems;
  }, [contextMenu.item, userCanEdit]);
  // --------------------------------------------------------------------------


  const renderFilterPrefix = useCallback(
    () => (
      <InputPrefix>
        <span className={`k-icon k-i-search ${styles['tree-filter-icon']}`} />
      </InputPrefix>
    ),
    [],
  );

  const renderFilterSuffix = useCallback(
    () => (
      <InputSuffix>
        {filterQuery && (
          <span
            className={`k-icon k-i-close ${styles['tree-filter-clear']}`}
            onClick={onClearFilter}
            role="button"
            aria-label="Clear search"
          />
        )}
      </InputSuffix>
    ),
    [filterQuery, onClearFilter],
  );

  const onExpandChange = useCallback(
    ({ item }: TreeViewExpandChangeEvent) => {
      let ids = expand.ids.slice();
      const index = ids.indexOf(item.text);

      index === -1 ? ids.push(item.text) : ids.splice(index, 1);
      setExpand({ ids, idField: 'text' });
      if (item.kind === StorageKind.folder) {
        setSelectedPath(item.path);
      }
    },
    [expand],
  );

  const onItemClick = useCallback(
    ({ item, syntheticEvent }: any) => {
      if (
        syntheticEvent.target.className === 'k-button-icon k-icon k-i-folder-add' ||
        syntheticEvent.target.className === 'k-button-icon k-icon k-i-file-add'
      ) {
        return;
      }

      // Folder clicks **only expand**, never collapse. To explicitly
      // collapse a folder the user clicks the arrow icon next to it,
      // which fires `onExpandChange` (still a toggle). This matches the
      // file-manager intuition where clicking a folder "opens" it but
      // doesn't fold it back on a second click.
      if (item.kind === StorageKind.folder && !expand.ids.includes(item.text)) {
        setExpand({ ids: [...expand.ids, item.text], idField: 'text' });
      }

      selectFile(item);
      // selectedItemIds is derived from selectFile via the memo above —
      // no need to track it here.

      if (item.kind === StorageKind.folder) {
        setSelectedPath(item.path);
      }
    },
    [expand, selectFile],
  );

  const onClickAddFolder = useCallback((item: StorageItem) => {
    if (item.path) {
      setSelectedPath(item.path);
    }
    setNewFolderVisible(true);
  }, []);

  const onSubmitAddFolder = useCallback(
    (result: any) => {
      addNewFolder(selectedPath, result.folderName);
      setNewFolderVisible(false);
    },
    [addNewFolder, selectedPath],
  );

  const onClickAddDocument = useCallback((item: StorageItem) => {
    if (item.path) {
      setSelectedPath(item.path);
    }

    setNewFileVisible(true);
  }, []);

  const onSubmitRenameDocument = useCallback(
    (item: StorageItem, newFileName: string) => {
      renameDocument(item, newFileName);
      setRenameItemVisible({ visible: false, title: '', item: null });
    },
    [renameDocument],
  );

  const onSubmitRenameFolder = useCallback(
    (item: StorageItem, newFolderName: string) => {
      renameFolder(item, newFolderName);
      setRenameItemVisible({ visible: false, title: '', item: null });
    },
    [renameFolder],
  );

  const onClickRenameDocument = useCallback(
    (item: StorageItem) => {
      setRenameItemVisible({
        visible: true,
        title: `Rename file — ${item.text}`,
        item,
        callBack: onSubmitRenameDocument,
      });
    },
    [onSubmitRenameDocument],
  );

  const onClickRenameFolder = useCallback(
    (item: StorageItem) => {
      setRenameItemVisible({
        visible: true,
        title: `Rename folder — ${item.text}`,
        item,
        callBack: onSubmitRenameFolder,
      });
    },
    [onSubmitRenameFolder],
  );

  const onClickShareDocument = useCallback(async (item: StorageItem) => {
    setSharedLinkVisible({ visible: true, item, callBack: () => setSharedLinkVisible({ visible: false }) });
  }, []);

  const onSubmitAddDocument = useCallback(
    (items: StorageItem[]) => {
      addNewDocument(selectedPath, items);
    },
    [addNewDocument, selectedPath],
  );

  const onClickDeleteDocument = useCallback(
    (item: StorageItem) => {
      deleteDocument(item);
    },
    [deleteDocument],
  );

  const onClickDeleteFolder = useCallback(
    (item: StorageItem) => {
      deleteFolder(item);
    },
    [deleteFolder],
  );

  const onClickDeleteContentFolder = useCallback(
    (item: StorageItem) => {
      deleteContentFolder(item);
    },
    [deleteContentFolder],
  );

  // Defined here (after all the local dialog-opening wrappers) so it can
  // dispatch context-menu actions through the same wrappers the toolbar uses.
  const onContextMenuSelect = useCallback(
    (e: MenuSelectEvent) => {
      const target = contextMenu.item;
      const action = (e.item as any)?.data?.action;
      closeContextMenu();
      if (!target || !action) return;
      const isDocument = target.kind === StorageKind.document;
      switch (action) {
        case 'rename':
          isDocument ? onClickRenameDocument(target) : onClickRenameFolder(target);
          break;
        case 'delete':
          isDocument ? onClickDeleteDocument(target) : onClickDeleteFolder(target);
          break;
        case 'deleteContent':
          onClickDeleteContentFolder(target);
          break;
        case 'share':
          onClickShareDocument(target);
          break;
        case 'addFolder':
          onClickAddFolder(target);
          break;
        case 'addFile':
          onClickAddDocument(target);
          break;
        case 'download':
          downloadDocument?.(target);
          break;
        case 'downloadFolder':
          downloadFolder?.(target);
          break;
        default:
          break;
      }
    },
    [
      contextMenu.item,
      closeContextMenu,
      onClickRenameDocument,
      onClickRenameFolder,
      onClickDeleteDocument,
      onClickDeleteFolder,
      onClickDeleteContentFolder,
      onClickShareDocument,
      onClickAddFolder,
      onClickAddDocument,
      downloadDocument,
      downloadFolder,
    ],
  );

  // ----- "More" menu next to the search box ---------------------------------
  // Replaces the previous DocumentsActions toolbar. Items adapt to the
  // currently selected tree item: folder selection gets folder-scoped
  // actions, document selection only gets Share. The button is hidden
  // entirely when nothing is selected or the user has no edit rights.
  const moreMenuItems = useMemo(() => {
    if (!selectedFile) return [];
    // The DropDownButton's `render` prop replaces the WHOLE inner content
    // (icon + text), so each item's render rebuilds both — Kendo's
    // ButtonItem only renders the icon when `render` is null.
    const buildItem = (text: string, iconName: string, action: string, shortcut?: string) => ({
      text,
      icon: iconName,
      action,
      render: () => (
        <>
          <span className={`k-icon k-i-${iconName}`} key="icon" style={{ marginRight: 6 }} />
          <span className="k-menu-link-text" key="text">
            {shortcut ? renderUnderlinedLabel(text, shortcut) : text}
          </span>
        </>
      ),
    });
    const isFolder = selectedFile.kind === StorageKind.folder;
    const canEdit = userCanEdit(selectedFile);
    if (isFolder) {
      const items: any[] = [];
      if (canEdit) {
        items.push(buildItem('New folder', 'folder-add', 'createFolder', 'n'));
        items.push(buildItem('Upload file', 'file-add', 'uploadFile', 'u'));
        items.push(buildItem('Delete content', 'files', 'deleteContent', 'o'));
        items.push(buildItem('Delete folder', 'delete', 'deleteFolder', 'd'));
        items.push(buildItem('Share', 'share', 'share', 's'));
      }
      items.push(buildItem('Download as ZIP', 'download', 'downloadFolder', 'z'));
      return items;
    }
    if (!canEdit) return [];
    return [buildItem('Share', 'share', 'share', 's')];
  }, [selectedFile, userCanEdit]);

  const onMoreMenuItemClick = useCallback(
    (e: DropDownButtonItemClickEvent) => {
      const action = (e.item as any)?.action;
      if (!selectedFile || !action) return;
      switch (action) {
        case 'createFolder':
          onClickAddFolder(selectedFile);
          break;
        case 'uploadFile':
          onClickAddDocument(selectedFile);
          break;
        case 'deleteFolder':
          onClickDeleteFolder(selectedFile);
          break;
        case 'deleteContent':
          onClickDeleteContentFolder(selectedFile);
          break;
        case 'share':
          onClickShareDocument(selectedFile);
          break;
        case 'downloadFolder':
          downloadFolder?.(selectedFile);
          break;
        default:
          break;
      }
    },
    [
      selectedFile,
      onClickAddFolder,
      onClickAddDocument,
      onClickDeleteFolder,
      onClickDeleteContentFolder,
      onClickShareDocument,
      downloadFolder,
    ],
  );
  // --------------------------------------------------------------------------

  // ----- Keyboard shortcuts to OPEN the menus -------------------------------
  // `m` opens the kebab "more actions" dropdown.
  // `c` opens the right-click context menu on the currently selected item.
  // Both single-letter shortcuts are gated by ignoreWhenTyping (default true)
  // so they don't fire while the user is typing in the search box.
  const moreButtonRef = useRef<DropDownButton>(null);

  const openMoreMenu = useCallback(() => {
    if (moreMenuItems.length === 0) return;
    moreButtonRef.current?.element?.click();
  }, [moreMenuItems]);

  const openContextMenuOnSelected = useCallback(() => {
    if (!selectedFile?.path) return;
    // Find the rendered DOM label for the selected item to anchor the menu.
    const labels = treeContainerRef.current?.querySelectorAll<HTMLElement>('.tree-item-label');
    const label = labels && Array.from(labels).find((el) => el.dataset.path === selectedFile.path);
    if (!label) return;
    const rect = label.getBoundingClientRect();
    openContextMenuAt(selectedFile, Math.round(rect.left + 8), Math.round(rect.bottom + 4));
  }, [selectedFile, openContextMenuAt]);

  useKeyboardShortcut({ key: 'm', handler: openMoreMenu });
  useKeyboardShortcut({ key: 'c', handler: openContextMenuOnSelected });

  // ----- Direct action shortcuts on the selected item -----------------------
  // n / u / r / d / o / s / w go straight to the matching menu action
  // without having to open the kebab or the context menu first. Each
  // shortcut is gated on the right combination of item type + edit rights
  // so e.g. `n` (new folder) only fires when a folder is selected and the
  // user has edit access; `o` (delete c**o**ntent) only fires on a folder
  // because that action only applies to folders; `w` (do**w**nload) only
  // fires on a document and is the one shortcut that does NOT require edit
  // rights, mirroring the "download is universal" rule of the right-click
  // menu. The destructive ones (`d`, `o`) surface their existing
  // Confirmation dialog, so a stray keystroke can't permanently destroy
  // anything.
  const selectedIsFolder = selectedFile?.kind === StorageKind.folder;
  const selectedIsDocument = selectedFile?.kind === StorageKind.document;
  const selectedCanEdit = Boolean(selectedFile && userCanEdit(selectedFile));

  const shortcutNewFolder = useCallback(() => {
    if (selectedFile) onClickAddFolder(selectedFile);
  }, [selectedFile, onClickAddFolder]);

  const shortcutUploadFile = useCallback(() => {
    if (selectedFile) onClickAddDocument(selectedFile);
  }, [selectedFile, onClickAddDocument]);

  const shortcutRename = useCallback(() => {
    if (!selectedFile) return;
    if (selectedIsDocument) onClickRenameDocument(selectedFile);
    else onClickRenameFolder(selectedFile);
  }, [selectedFile, selectedIsDocument, onClickRenameDocument, onClickRenameFolder]);

  const shortcutDelete = useCallback(() => {
    if (!selectedFile) return;
    if (selectedIsDocument) onClickDeleteDocument(selectedFile);
    else onClickDeleteFolder(selectedFile);
  }, [selectedFile, selectedIsDocument, onClickDeleteDocument, onClickDeleteFolder]);

  const shortcutDeleteContent = useCallback(() => {
    if (selectedFile) onClickDeleteContentFolder(selectedFile);
  }, [selectedFile, onClickDeleteContentFolder]);

  const shortcutShare = useCallback(() => {
    if (selectedFile) onClickShareDocument(selectedFile);
  }, [selectedFile, onClickShareDocument]);

  const shortcutDownload = useCallback(() => {
    if (selectedFile) downloadDocument?.(selectedFile);
  }, [selectedFile, downloadDocument]);

  const shortcutDownloadFolder = useCallback(() => {
    if (selectedFile) downloadFolder?.(selectedFile);
  }, [selectedFile, downloadFolder]);

  useKeyboardShortcut({
    key: 'n',
    handler: shortcutNewFolder,
    enabled: selectedCanEdit && selectedIsFolder,
  });
  useKeyboardShortcut({
    key: 'u',
    handler: shortcutUploadFile,
    enabled: selectedCanEdit && selectedIsFolder,
  });
  useKeyboardShortcut({
    key: 'r',
    handler: shortcutRename,
    enabled: selectedCanEdit && Boolean(selectedFile),
  });
  useKeyboardShortcut({
    key: 'd',
    handler: shortcutDelete,
    enabled: selectedCanEdit && Boolean(selectedFile),
  });
  useKeyboardShortcut({
    key: 'o',
    handler: shortcutDeleteContent,
    enabled: selectedCanEdit && selectedIsFolder,
  });
  useKeyboardShortcut({
    key: 's',
    handler: shortcutShare,
    enabled: selectedCanEdit && Boolean(selectedFile),
  });
  useKeyboardShortcut({
    key: 'w',
    handler: shortcutDownload,
    enabled: selectedIsDocument && Boolean(downloadDocument),
  });
  useKeyboardShortcut({
    key: 'z',
    handler: shortcutDownloadFolder,
    enabled: selectedIsFolder && Boolean(downloadFolder),
  });
  // --------------------------------------------------------------------------

  return (
    <>
      <div className={styles['tree-toolbar']}>
        <TextBox
          ref={searchBoxRef}
          className={styles['tree-filter']}
          placeholder="Search documents… (press / to focus)"
          value={filterQuery}
          onChange={onFilterChange}
          prefix={renderFilterPrefix}
          suffix={renderFilterSuffix}
        />
        {moreMenuItems.length > 0 && (
          <DropDownButton
            ref={moreButtonRef}
            icon="more-vertical"
            fillMode="flat"
            title="More actions (press m)"
            className={styles['tree-more-button']}
            items={moreMenuItems}
            onItemClick={onMoreMenuItemClick}
          />
        )}
      </div>
      <div ref={treeContainerRef} className={styles['tree-container']}>
        {filterQuery.trim() && visibleItems.length === 0 ? (
          <div className={styles['tree-empty-state']} role="status">
            <span className={`k-icon k-i-zoom-out ${styles['tree-empty-icon']}`} />
            <p className={styles['tree-empty-title']}>No documents match</p>
            <p className={styles['tree-empty-hint']}>
              No results for <strong>“{filterQuery}”</strong>. Try different keywords.
            </p>
            <button
              type="button"
              className={styles['tree-empty-action']}
              onClick={onClearFilter}
            >
              Clear search
            </button>
          </div>
        ) : (
          <Tooltip openDelay={400} position="right" anchorElement="target">
            <TreeView
              data={processTreeViewItems(visibleItems, {
                expand: effectiveExpand,
                select: selectedItemIds,
              })}
              className={styles['tree-view']}
              expandIcons={true}
              onExpandChange={onExpandChange}
              onItemClick={onItemClick}
              aria-multiselectable={false}
              checkboxes={false}
              tabIndex={0}
              item={(props: any) => <TreeViewItem {...props} />}
            />
          </Tooltip>
        )}
      </div>
      {newFolderVisible === true ? (
        <AddFolder
          onSubmit={(result: any) => onSubmitAddFolder(result)}
          onCancel={() => setNewFolderVisible(false)}
          parentFolderName={selectedFile?.text}
        />
      ) : (
        <></>
      )}
      {renameItemVisible.visible === true ? (
        <RenameItem
          onSubmit={(result: any) => renameItemVisible.callBack(renameItemVisible.item, result.name)}
          onCancel={() => setRenameItemVisible({ visible: false, title: '', item: null })}
          title={renameItemVisible.title}
          currentName={renameItemVisible.item.text}
        />
      ) : (
        <></>
      )}
      {newFileVisible === true ? (
        <UploadFile
          onSubmit={(result: any) => onSubmitAddDocument(result)}
          onCancel={() => setNewFileVisible(false)}
          onFinishUpload={() => {
            setNewFileVisible(false);
          }}
          folderName={selectedPath}
          parentFolderName={selectedFile?.text}
          test={'dummy'}
        />
      ) : (
        <></>
      )}
      {sharedLinkVisible.visible && (
        <SharedLinkCreator
          data-testid="confirmation-dialog"
          onClose={sharedLinkVisible.callBack}
          item={sharedLinkVisible.item}
        />
      )}
      <ContextMenu
        show={contextMenu.visible}
        offset={contextMenu.offset}
        items={contextMenuItems}
        onSelect={onContextMenuSelect}
        onClose={closeContextMenu}
      />
    </>
  );
};

export default TreeViewDocuments;
